/**
 * Variables disponibles dans un modèle de SMS.
 *
 * Le jeu est celui des mailings — un agent qui passe du mail au SMS retrouve les
 * mêmes noms — mais les **valeurs** y sont rendues courtes : un mail peut se
 * permettre « 08 septembre 2026 », un SMS non.
 *
 * Chaque variable porte donc un **budget** : la longueur de la valeur la plus
 * longue qu'elle puisse prendre. La validation d'un modèle substitue ces
 * longueurs maximales avant de compter — c'est ce qui empêche d'enregistrer un
 * texte à 158 caractères qui partira à 167 le jour où la référence fait 6
 * caractères au lieu de 4.
 *
 * Le budget est un contrat avec le résolveur côté serveur : allonger un format
 * (passer `paymentDeadlineDate` de `08/09` à `08/09/2026`) sans monter le budget
 * ici fait mentir tous les compteurs à la fois.
 */
import { SHORT_LINK_MAX_CHARS } from '../helpers/short-link.js';
/**
 * Échantillon d'un lien court, calé sur {@link SHORT_LINK_MAX_CHARS} : l'hôte
 * le plus long en service, celui de la recette.
 *
 * Les deux variables de lien ne portent plus l'URL complète : le serveur la
 * raccourcit avant de rendre. `paymentLink` faisait 139 caractères sur 160 — il
 * ne laissait pas de quoi écrire une phrase, et la variable était en pratique
 * inutilisable. Le budget est désormais celui du lien court, quelle que soit la
 * longueur de la cible.
 */
const SHORT_LINK_SAMPLE = 'staging.api.massiliavoyages.fr/l/Ab3dK9x';
export const SMS_TEMPLATE_VARIABLES = {
    /** Téléphone de l'agence propriétaire du dossier, dans sa graphie de contact. */
    agencyPhone: {
        budget: 14,
        hint: "Celui de l'agence qui suit le dossier : Marseille ou La Réunion.",
        label: "Téléphone de l'agence",
        sample: '04 91 91 06 98',
    },
    /**
     * Le PNR, jamais la référence interne : `MV-2026-00042` fait 13 caractères et
     * ferait sauter le budget de 7. Un dossier sans PNR voit son envoi refusé
     * plutôt que tronqué (cf. `sms.errors.ts`, `PNR_MISSING`).
     */
    bookingReference: {
        budget: 6,
        hint: 'Le PNR de la compagnie. Un dossier qui n’en a pas encore ne pourra pas recevoir ce SMS.',
        label: 'Référence dossier (PNR)',
        sample: 'ABC123',
    },
    /**
     * Lien vers la page dossier de l'espace client. Plus du tiers d'un SMS :
     * utilisable, mais il ne reste ensuite qu'environ 80 caractères de texte.
     */
    clientSpaceUrl: {
        budget: SHORT_LINK_MAX_CHARS,
        hint: 'Ouvre la page du dossier. Raccourci automatiquement, quelle que soit la longueur du lien.',
        label: "Lien vers l'espace client",
        sample: SHORT_LINK_SAMPLE,
    },
    /** Code IATA de l'aéroport d'arrivée du voyage. */
    destinationIata: {
        budget: 3,
        hint: 'Le code à trois lettres imprimé sur le billet (RUN, CDG…).',
        label: "Destination (code IATA d'arrivée)",
        sample: 'RUN',
    },
    /** Montant du dernier versement reçu, arrondi à l'euro. */
    lastPaymentAmount: {
        budget: 8,
        label: 'Montant du dernier paiement',
        sample: '1234 EUR',
    },
    /** Date du dernier versement reçu, au format court. */
    lastPaymentDate: { budget: 5, label: 'Date du dernier paiement (JJ/MM)', sample: '31/12' },
    /** Acompte minimum à régler, arrondi à l'euro supérieur. */
    minimumPaymentAmount: { budget: 8, label: 'Acompte minimum', sample: '1234 EUR' },
    /** Date limite de paiement, au format court `JJ/MM` — 5 caractères, pas 18. */
    paymentDeadlineDate: {
        budget: 5,
        hint: 'Sans l’année, pour tenir en 5 caractères. Une relance porte sur une échéance proche.',
        label: 'Date limite de paiement (JJ/MM)',
        sample: '31/12',
    },
    /** Heure limite de paiement : 11h00 le week-end, 16h00 en semaine. */
    paymentDeadlineTime: { budget: 5, label: 'Heure limite de paiement', sample: '16h00' },
    /**
     * Lien de paiement de l'acompte.
     *
     * Sa cible fait jusqu'à 139 caractères — l'adresse e-mail et le nom du client
     * sont dans la query string, et n'ont pas de longueur maximale. Elle ne part
     * jamais telle quelle : le serveur la raccourcit, et c'est ce raccourci de 24
     * caractères que le modèle doit budgéter.
     */
    paymentLink: {
        budget: SHORT_LINK_MAX_CHARS,
        hint: 'Ouvre directement le paiement de l’acompte. Raccourci automatiquement.',
        label: "Lien de paiement de l'acompte",
        sample: SHORT_LINK_SAMPLE,
    },
    /** Reste à payer, même format et même budget que l'acompte. */
    remainingAmount: { budget: 8, label: 'Montant restant dû', sample: '1234 EUR' },
    /** Délai restant avant l'échéance, en jours. */
    remainingDelay: {
        budget: 10,
        hint: 'En jours (« 12 jours »), ou « sans délai » quand l’échéance est passée.',
        label: "Délai restant avant l'échéance",
        sample: 'sans délai',
    },
};
/**
 * Syntaxe des variables : `{{ nom }}`, espaces intérieurs optionnels.
 *
 * Volontairement la même forme que les templates Brevo, pour qu'un agent qui
 * passe du mail au SMS n'ait pas deux notations en tête. La substitution, elle,
 * est faite chez nous — Brevo ne voit qu'un texte déjà rendu.
 */
export const SMS_VARIABLE_PATTERN = /\{\{\s*([A-Za-z][A-Za-z0-9_]*)\s*\}\}/g;
export function isSmsTemplateVariable(name) {
    return name in SMS_TEMPLATE_VARIABLES;
}
/** Noms de variables cités par un corps de modèle, dédoublonnés, dans l'ordre. */
export function smsVariablesUsedIn(body) {
    const found = [];
    const seen = new Set();
    for (const match of body.matchAll(SMS_VARIABLE_PATTERN)) {
        const name = match[1];
        if (!name || seen.has(name))
            continue;
        seen.add(name);
        found.push(name);
    }
    return found;
}
/**
 * Valeurs d'essai pour mesurer le pire cas d'un modèle : chaque variable connue
 * prend son échantillon (calé sur son budget), chaque variable inconnue une
 * chaîne de 10 caractères — assez pour que l'auteur voie que son modèle déborde
 * sans qu'on lui promette qu'une variable inventée existera à l'envoi.
 */
export function worstCaseSmsParams(body) {
    return Object.fromEntries(smsVariablesUsedIn(body).map((name) => [
        name,
        isSmsTemplateVariable(name) ? SMS_TEMPLATE_VARIABLES[name].sample : 'X'.repeat(10),
    ]));
}
